import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { createSilentError } from '~shared/lib/create-silent-error';

import { Button } from '@breezeventures/ui-kit/button';
import { Modal } from '@breezeventures/ui-kit/modal';

type ConfirmModalProps = {
  title: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  acceptOnly?: boolean;
  invertButtons?: boolean;
  onCancel?: () => void;
};

type ModalEntry = {
  id: string;
  props: ConfirmModalProps;
  resolve: () => void;
  reject: (reason?: unknown) => void;
};

interface Confirmation {
  confirm: (props: ConfirmModalProps) => Promise<void>;
}

const noop: Confirmation = { confirm: () => Promise.resolve() };

const ConfirmationContext = createContext<Confirmation>(noop);

export const ConfirmationProvider = ({ children }: PropsWithChildren) => {
  const [stack, setStack] = useState<ModalEntry[]>([]);

  const confirm = useCallback((props: ConfirmModalProps) => {
    return new Promise<void>((resolve, reject) => {
      setStack((prev) => [
        ...prev,
        { id: crypto.randomUUID(), props, resolve, reject },
      ]);
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setStack((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const handleClose = useCallback(
    (entry: ModalEntry) => {
      if (entry.props.acceptOnly) {
        entry.resolve();
      } else {
        entry.reject(createSilentError('Action cancelled'));
      }

      dismiss(entry.id);
    },
    [dismiss]
  );

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}

      {stack.map((entry) => {
        const {
          acceptOnly,
          cancelButtonText = 'Cancel',
          confirmButtonText = 'Ok',
          description,
          title,
          invertButtons = false,
          onCancel,
        } = entry.props;

        return (
          <Modal
            key={entry.id}
            opened
            onClose={() => handleClose(entry)}
            dismissible={!acceptOnly}
            title={title}
            description={description}
          >
            {({ close }) => {
              const confirmButton = (
                <Button
                  size="l"
                  variant={
                    acceptOnly || invertButtons ? 'primary' : 'secondary'
                  }
                  onClick={() => {
                    entry.resolve();
                    dismiss(entry.id);
                  }}
                >
                  {confirmButtonText}
                </Button>
              );

              const cancelButton = !acceptOnly && (
                <Button
                  size="l"
                  variant={invertButtons ? 'secondary' : 'primary'}
                  onClick={() => {
                    if (onCancel) {
                      onCancel();
                    }
                    close();
                  }}
                >
                  {cancelButtonText}
                </Button>
              );

              return (
                <Modal.FooterActions>
                  {invertButtons ? (
                    <>
                      {cancelButton}
                      {confirmButton}
                    </>
                  ) : (
                    <>
                      {confirmButton}
                      {cancelButton}
                    </>
                  )}
                </Modal.FooterActions>
              );
            }}
          </Modal>
        );
      })}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error('useConfirmation must be used within confirmation context');
  }

  return context;
};
